import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageSquare, Loader2, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

/**
 * GHL SMS test card — sends a single proof-of-delivery SMS via
 * POST /api/ghl/sms-test. Lives on /admin/settings just below
 * the main GHL config card so admins can verify end-to-end delivery
 * without leaving the settings screen.
 *
 * GHL only messages phones that are already synced to a Prestigious user
 * (Contact ID lookup). The backend surfaces that reason clearly if the
 * number doesn't match, so admins know to sync the contact first.
 */
export function GhlSmsTestCard() {
  const [to, setTo] = useState("");
  const [body, setBody] = useState(
    "Prestigious test message — GHL SMS delivery is live. Reply STOP to opt out."
  );
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async () => {
    const trimmed = (to || "").trim();
    if (!trimmed) {
      toast.error("Enter a phone number first");
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/ghl/sms-test", {
        to: trimmed,
        body: body?.trim() || undefined,
      });
      setLastResult(data);
      if (data.ok) {
        toast.success(`SMS sent via ${data.provider || "GHL"} → •••${data.to_last4}`);
      } else if (data.queued) {
        toast.warning(`Queued — ${data.error || "no matching Prestigious contact"}`);
      } else {
        toast.error(data.error || "Send failed");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Send failed";
      setLastResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const syncAndRetry = async () => {
    const uid = lastResult?.matched_user?.id;
    if (!uid) return;
    setSyncing(true);
    try {
      await api.post(`/ghl/sync-contact/${uid}`);
      toast.success(`Synced ${lastResult.matched_user.name} to GHL — retrying send`);
      await send();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="ghl-sms-test-card">
      <div className="flex items-center gap-2 overline mb-3">
        <MessageSquare className="h-4 w-4 text-orange-400" />
        Test SMS delivery (GHL)
      </div>
      <p className="text-sm text-zinc-400 mb-4">
        Send a proof-of-delivery SMS through your GoHighLevel account. The
        recipient must already be a synced Prestigious user — GHL only messages
        known contacts.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>To (E.164)</Label>
          <Input
            data-testid="ghl-sms-test-to"
            placeholder="+15551234567"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={busy}
          />
        </div>
        <div>
          <Label>Message</Label>
          <Textarea
            data-testid="ghl-sms-test-body"
            className="min-h-[64px]"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button
          data-testid="ghl-sms-test-send"
          onClick={send}
          disabled={busy}
          className="rounded-full bg-orange-500 hover:bg-orange-600 text-black"
        >
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
            </>
          ) : (
            <>Send test SMS</>
          )}
        </Button>
        {lastResult?.ok && (
          <span
            data-testid="ghl-sms-test-ok"
            className="inline-flex items-center gap-1.5 text-emerald-400 text-sm"
          >
            <CheckCircle2 className="h-4 w-4" />
            Delivered via {lastResult.provider || "GHL"} · •••{lastResult.to_last4}
          </span>
        )}
        {lastResult?.queued && (
          <span
            data-testid="ghl-sms-test-queued"
            className="inline-flex items-center gap-1.5 text-amber-400 text-sm"
          >
            <AlertCircle className="h-4 w-4" />
            Queued — {lastResult.error}
          </span>
        )}
        {lastResult && !lastResult.ok && !lastResult.queued && (
          <span
            data-testid="ghl-sms-test-fail"
            className="inline-flex items-center gap-1.5 text-red-400 text-sm"
          >
            <AlertCircle className="h-4 w-4" />
            {lastResult.error || "Send failed"}
          </span>
        )}
      </div>

      {lastResult?.matched_user && !lastResult.ok && (
        <div
          className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-center justify-between gap-3"
          data-testid="ghl-sms-test-sync-nudge"
        >
          <div className="text-xs">
            <span className="text-amber-300 font-medium">
              {lastResult.matched_user.name}
            </span>{" "}
            <span className="text-zinc-400">
              is a Prestigious {lastResult.matched_user.role || "user"} but{" "}
              {lastResult.matched_user.has_ghl_contact
                ? "the GHL contact needs a re-sync."
                : "isn't synced to GoHighLevel yet."}
            </span>
          </div>
          <Button
            data-testid="ghl-sms-test-sync-btn"
            onClick={syncAndRetry}
            disabled={syncing || busy}
            size="sm"
            className="rounded-full bg-amber-500 hover:bg-amber-600 text-black shrink-0"
          >
            {syncing ? (
              <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Syncing…</>
            ) : (
              <><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Sync &amp; retry</>
            )}
          </Button>
        </div>
      )}
    </Card>
  );
}

export default GhlSmsTestCard;
